import { ContainerClient } from '@azure/storage-blob';
import { Box, Flex, IconButton, Input, Spinner, Text, Tooltip, useToast } from '@chakra-ui/react';
import { taskAction, uploadAction } from 'actions';
import { Promise as BluebirdPromise } from 'bluebird';
import InputSearch from 'components/InputSearch';
import { FILE_EXTENSION_UPLOAD_ALLOWED } from 'containers/Upload/UploadInput';
import { MAX_CONCURRENT_REQUEST, MAXIMUM_SUMMARY_FILES_SELECT, TEMPORARY } from 'helpers/constant';
import { TASK_FILE_STATUS, TASK_STATUS, UPLOAD_FILES_STATUS } from 'helpers/enum';
import {
  formatBytes,
  formatBytestoKBytes,
  getFileExtensionFromFileName,
  removeQueryString,
} from 'helpers/file';
import { Check, FileText, Paperclip, TriangleAlert, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentService } from 'services/document/document.service';
import { FileStore } from 'types/actions/upload';
import { FileDocument } from 'types/reducers/document';
import { TaskFileDocument } from 'types/reducers/task';
import { getBlobContainer } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

interface SelectFileProps {
  onOpenFile: (_file: TaskFileDocument) => void;
}

const SelectFilesMobile = ({ onOpenFile }: SelectFileProps) => {
  const uploadMobileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const toast = useToast();

  const dispatch = useDispatch();
  const {
    current,
    status: taskStatus,
    processingStreamingChunkStatusTask,
  } = useSelector((store) => store.task);
  const { stats, status, files, blobUrl } = useSelector((state) => state.upload);

  const selectFilesMobileCount = current.selectedFiles.length ?? 0;
  const isWaitingProcessFileUpload = current.isWaitingProcessFileUpload;
  const isUploadingFiles = status === UPLOAD_FILES_STATUS.UPLOADING || isWaitingProcessFileUpload;
  const isProcessingTask = taskStatus === TASK_STATUS.GENERATE_ANSWER;
  const isDisableUpload =
    !current.task_id || isUploadingFiles || isWaitingProcessFileUpload || isProcessingTask;
  const isDisableSelect = !current.task_id || isProcessingTask;

  const isReachMaximumFilesSelect = selectFilesMobileCount >= MAXIMUM_SUMMARY_FILES_SELECT;

  const searchFiles = async (value: string) => {
    return DocumentService.getListFiles({
      page: 0,
      size: 10,
      file_name: value,
      sort_field: 'created_at',
      is_ascending: 'false',
      status: 'PROCESSED',
    });
  };

  const showMaximumFileSelectWarning = () => {
    toast({
      title: `Maximum ${MAXIMUM_SUMMARY_FILES_SELECT} files selected!`,
      status: 'warning',
      position: 'bottom-right',
      isClosable: true,
    });
  };

  const onSelectFile = (newFile: FileDocument) => {
    if (isReachMaximumFilesSelect) {
      return showMaximumFileSelectWarning();
    }

    dispatch(
      taskAction.addSelectedFile({ ...newFile, task_file_status: TASK_FILE_STATUS.SELECTED })
    );
  };

  const onRemoveFile = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    deleteFile: TaskFileDocument
  ) => {
    e.stopPropagation();
    // Block remove file when processing task

    if (isProcessingTask || processingStreamingChunkStatusTask) {
      return;
    }
    dispatch(taskAction.removeSelectedFile(deleteFile));
  };

  const handleMobileUpload = () => {
    uploadMobileInputRef.current?.click();
  };

  const onChangeFileMobile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (processingStreamingChunkStatusTask) {
      return;
    }
    if (isReachMaximumFilesSelect) {
      return showMaximumFileSelectWarning();
    }
    const files = (e.target?.files ?? []) as File[];
    if (!FILE_EXTENSION_UPLOAD_ALLOWED.includes(getFileExtensionFromFileName(files[0]?.name))) {
      toast({
        title: t('upload.fileIsNotValid'),
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    dispatch(uploadAction.startUpload(files));
    dispatch(taskAction.updateCurrentTask({ isWaitingProcessFileUpload: true }));

    const currentTaskId = current.task_id;
    setTimeout(() => {
      if (current.isWaitingProcessFileUpload && current.task_id === currentTaskId) {
        dispatch(taskAction.updateCurrentTask({ isWaitingProcessFileUpload: false }));
      }
    }, 60000);
  };

  const uploadFileToBlobStorage = async (containerClient: ContainerClient, file: File) => {
    const blobClient = containerClient.getBlockBlobClient(uuidv4() + '_' + file.name);

    const blobHTTPHeaders = { blobContentType: file.type };

    return blobClient
      .uploadData(file, {
        blockSize: 20 * 1024 * 1024,
        concurrency: 40,
        blobHTTPHeaders,
      })
      .then(() => {
        dispatch(uploadAction.uploadSuccess());
        return {
          url: removeQueryString(blobClient.url),
          name: file.name,
          size: formatBytestoKBytes(file.size, 2),
        };
      })
      .catch(() => {
        dispatch(uploadAction.uploadFail(file));
      });
  };

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.UPLOADING && blobUrl && files && files?.length > 0) {
      const containerClient = getBlobContainer(blobUrl);
      for (const file of files) {
        dispatch(
          taskAction.addSelectedFile({
            id: uuidv4(),
            name: file.name,
            url: '',
            size: parseFloat(formatBytestoKBytes(file.size, 2)),
            state: '',
            created_by: '',
            created_date: '',
            org: '',
            task_file_status: TASK_FILE_STATUS.UPLOADED,
          })
        );
      }
      toast({
        title: t('upload.startUploading', { total: stats.total }),
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });

      const uploadedFile: FileStore[] = [];
      BluebirdPromise.map(
        files,
        async (file) => {
          return uploadFileToBlobStorage(containerClient, file).then((file) => {
            if (file) {
              uploadedFile.push(file);
            }
          });
        },
        { concurrency: MAX_CONCURRENT_REQUEST }
      )
        .then(() => {
          dispatch(
            uploadAction.completeUploadFile({
              files: uploadedFile,
              isTaskUpload: true,
              task_id: current.type !== TEMPORARY ? current.task_id : undefined,
            })
          );
          dispatch(
            taskAction.updateSelectedFile({
              data: { url: uploadedFile[0]?.url, task_file_status: TASK_FILE_STATUS.UPLOADED },
              isTemporary: current.type === TEMPORARY,
            })
          );
          dispatch(uploadAction.completeUpload());
        })
        .catch((e: Error) => console.log(e));
    }
  }, [blobUrl, status, files]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.FINISH) {
      if (uploadMobileInputRef.current) uploadMobileInputRef.current.value = '';
      if (stats.uploadSuccess > 0) {
        toast({
          title: t('upload.uploadedSuccessfullyProcessing', {
            uploadSuccess: stats.uploadSuccess,
            total: stats.total,
          }),
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      }

      if (stats.uploadFail > 0) {
        toast({
          title: t('upload.uploadedFailed', { uploadFail: stats.uploadFail, total: stats.total }),
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      }
      dispatch(uploadAction.resetUpload());
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Flex align='center' justify='space-between' mb={3}>
        <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
          {t('summary.selectFiles')}
        </Text>
        <Flex
          align='center'
          h='22px'
          px={2}
          color={isReachMaximumFilesSelect ? 'state.warning' : 'primary.solid'}
          fontFamily='mono'
          fontSize='xs'
          fontWeight={600}
          bg={isReachMaximumFilesSelect ? 'state.warningSoft' : 'primary.soft'}
          borderRadius='sm'
        >
          {selectFilesMobileCount}/{MAXIMUM_SUMMARY_FILES_SELECT}
        </Flex>
      </Flex>

      <Flex gap={2} w='100%'>
        <Box flex='1' minW={0}>
          <InputSearch<FileDocument>
            callback={searchFiles}
            onSelect={onSelectFile}
            disabled={isDisableSelect}
          />
        </Box>
        <Tooltip
          label={
            isWaitingProcessFileUpload ? t('summary.fileIsProcessing') : t('summary.uploadFiles')
          }
        >
          <IconButton
            aria-label={t('summary.uploadFiles')}
            icon={isUploadingFiles ? <Spinner size='sm' /> : <Paperclip size={18} />}
            isDisabled={isDisableSelect}
            onClick={handleMobileUpload}
            size='ds-sm'
            variant='ds-outline'
          />
        </Tooltip>
        <Input
          ref={uploadMobileInputRef}
          display='none'
          accept={FILE_EXTENSION_UPLOAD_ALLOWED.join(', ')}
          disabled={isDisableUpload}
          onChange={onChangeFileMobile}
          type='file'
        />
      </Flex>

      {isReachMaximumFilesSelect && (
        <Flex align='center' gap={2} mt={3} color='state.warning' fontSize='xs'>
          <TriangleAlert size={13} />
          <Text>Maximum {MAXIMUM_SUMMARY_FILES_SELECT} files selected</Text>
        </Flex>
      )}

      {selectFilesMobileCount === 0 ? (
        <Text mt={4} px={3} py={6} color='text.muted' fontSize='sm' textAlign='center'>
          No files selected yet. Search above or upload to add up to {MAXIMUM_SUMMARY_FILES_SELECT}.
        </Text>
      ) : (
        <Flex direction='column' gap={2} mt={4}>
          {current?.selectedFiles?.map((file) => {
            const isSaved = file.task_file_status === TASK_FILE_STATUS.SAVED;
            const isUploaded = file.task_file_status === TASK_FILE_STATUS.UPLOADED;
            return (
              <Flex
                key={`mobile-${file.id}`}
                align='center'
                gap={3}
                px={3}
                py={3}
                bg='bg.surface'
                borderWidth='1px'
                borderColor={isSaved ? 'primary.solid' : 'border.default'}
                borderRadius='md'
                _hover={{ borderColor: 'primary.solid', shadow: 'ds_xs' }}
                cursor='pointer'
                transition='all .14s ease-out'
                onClick={() => onOpenFile(file)}
              >
                <Flex
                  align='center'
                  justify='center'
                  flexShrink={0}
                  w='32px'
                  h='32px'
                  color='primary.solid'
                  bg='primary.soft'
                  borderRadius='sm'
                >
                  <FileText size={15} />
                </Flex>
                <Tooltip label={file.name} placement='top-start'>
                  <Box flex={1} minW={0}>
                    <Text
                      overflow='hidden'
                      color='text.default'
                      fontSize='sm'
                      fontWeight={500}
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                    >
                      {file.name}
                    </Text>
                    <Text color='text.subtle' fontSize='xs'>
                      {formatBytes(file.size * 1024)}
                    </Text>
                  </Box>
                </Tooltip>
                {isUploaded && (
                  <Tooltip label={t('summary.fileIsProcessing')}>
                    <Box flexShrink={0}>
                      <Spinner color='text.subtle' size='sm' />
                    </Box>
                  </Tooltip>
                )}
                {isSaved && (
                  <Tooltip label={t('summary.fileIsSaved')}>
                    <Box flexShrink={0} color='state.success' aria-label={t('summary.fileIsSaved')}>
                      <Check size={16} />
                    </Box>
                  </Tooltip>
                )}
                <Tooltip label={t('common.cancel')}>
                  <IconButton
                    flexShrink={0}
                    aria-label={t('common.cancel')}
                    icon={<X size={15} />}
                    onClick={(e) => onRemoveFile(e, file)}
                    size='ds-sm'
                    variant='ds-ghost'
                  />
                </Tooltip>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default SelectFilesMobile;
